import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import Avatar from '../Avatar';
import ButtonGroup from '../buttons/ButtonGroup';
import { tsSince } from '../../common';
import { getTextColor, type, getTextType, getBorders, getColor, getBackgroundTheme } from '../../style';
import { Link, useNavigate } from 'react-router-dom';
import { HighlightFavoriteButton } from '../buttons/FavoriteButton';
import { generalIcons } from '../icons/materialIcons';
import { getBrowserDetails, ternaryFunc, triggerTracking } from '../../utils/helpers';
import { ignoreOffer, counterOffer, acceptOffer, cancelOffer } from '../../actions/offerTrackAction';
import { Currency, IOfferListItemProps, IsOfferHighlightProp } from './types/offerListItem';
import _isEmpty from 'lodash/isEmpty';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';

const ItemContainer = styled('li')<IsOfferHighlightProp>(
  () => ({
    display: 'block',
    listStyle: 'none',
    borderBottom: getBorders().DEFAULT,
    ':last-of-type': {
      borderBottom: 'none',
    },
  }),
  ({ isOfferHighlight }) => ({
    borderBottom: isOfferHighlight ? 'none' : getBorders().DEFAULT,
  })
);

const Item = styled('div')<IsOfferHighlightProp>(
  {
    display: 'flex',
    [breakpoints.down(479.05)]: {
      display: 'none',
    },
  },
  ({ isOfferHighlight }) => ({
    padding: isOfferHighlight ? 0 : 16,
  })
);

const DetailsActionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flex: 1,
});

const OfferDetails = styled('div')<Pick<IsOfferHighlightProp, keyof IsOfferHighlightProp>>(
  () => ({
    color: getTextColor().DARK.color,
  }),
  ({ isOfferHighlight }) => ({
    padding: isOfferHighlight ? 10 : 0,
    [breakpoints.down(479.05)]: {
      padding: 10,
    },
  })
);

const Username = styled('span')(() => ({
  color: getTextColor().DARKER.color,
  fontWeight: 600,
}));

const OfferDescription = styled('span')(() => ({
  color: getTextColor().DARKER.color,
}));

//keep price separated

const OfferLabel = styled('div')<{ isOfferHighlight: boolean; children?: string }>(
  () => ({
    fontSize: '14px',
    color: getTextColor().DARKEST.color,
    [breakpoints.down(479.05)]: {
      color: '#FFFFFF',
    },
  }),
  ({ isOfferHighlight }: { isOfferHighlight: boolean }) =>
    isOfferHighlight && {
      ...getTextType().header,
    }
);

const OfferPriceDesktop = styled('div')({
  textAlign: 'center',
  display: 'block',

  [breakpoints.down(479.05)]: {
    display: 'none',
  },
});

const OfferPriceMobile = styled('div')({
  textAlign: 'right',
  display: 'none',
  [breakpoints.down(479.05)]: {
    display: 'block',
  },
});

const OfferPrice = styled('div')({
  textAlign: 'right',
});

const PriceLabel = styled('div')<IsOfferHighlightProp>(
  {
    paddingTop: '2px',
    verticalAlign: 'top',

    display: 'inline-block',
    fontSize: '14pxß',
  },
  ({ isOfferHighlight }) =>
    isOfferHighlight && {
      lineHeight: '30px',
      color: '#1383d2',
      fontWeight: 600,
      ...type.XL,
    }
);

const Price = styled('div')<IsOfferHighlightProp>(
  {
    fontSize: '32px',
    lineHeight: '32px',

    display: 'inline-block',
  },
  ({ isOfferHighlight }) =>
    isOfferHighlight && {
      color: '#1383d2',
      fontSize: type.XXXL.fontSize,
      fontWeight: 600,
      lineHeight: type.XXXL.fontSize,
    }
);

// OFFER CARD

const ItemContainerMobile = styled('li')({
  listStyle: 'none',
});

const Card = styled('div')<IsOfferHighlightProp>(({ isOfferHighlight }) => ({
  display: 'none',
  background: getBackgroundTheme().block,
  marginBottom: '22px',
  borderBottom: getBorders().DEFAULT,
  [breakpoints.down(479.05)]: {
    display: isOfferHighlight ? 'none' : 'block',
    marginBottom: isOfferHighlight ? 0 : 12,
  },
}));

const CardTop = styled('div')({
  position: 'relative',
});

const CardOfferPrice = styled(OfferPriceMobile)({
  position: 'absolute',
  bottom: '20px',
  right: '12px',
  color: 'white',
  zIndex: 2,
});

const Gradient = styled('div')({
  background:
    'rgba(0, 0, 0, 0) linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(255, 255, 255, 0) 30%) repeat scroll 0% 0%',
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  zIndex: 1,
});

const CardBottom = styled('div')({
  padding: 16,
  [breakpoints.down('md')]: {
    padding: 0,
  },
});

const StyledSwipeContainer = styled('div')(
  {
    display: 'none !important',
    height: 0,
    [breakpoints.down(639.05)]: {
      display: 'block !important',
    },
  },
  ({ highlightMobile }: CustomStyledProps) =>
    highlightMobile && {
      height: '100%',
    }
);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const HighlightsButtonGroup = styled('div')(() => ({
  textAlign: 'center',
  padding: '14px 0',
  border: `solid 1px ${getColor().BRAND_1}`,
  boxShadow: `0 0 1px 0 ${getColor().BRAND_1}`,
  borderRadius: 10,
  background: getBackgroundTheme().block,
  [breakpoints.down(479.05)]: {
    border: 'none',
    boxShadow: 'none',
  },
}));

const OfferIcons = styled('div')<IsOfferHighlightProp>(
  {
    display: 'inline-flex',
    marginTop: 10,
  },
  ({ isOfferHighlight }) =>
    isOfferHighlight && {
      svg: {
        width: '64px !important',
        height: '64px !important',
      },
    }
);

const ButtonGroupContainer = styled('div')({
  padding: 0,
  [breakpoints.down(479.05)]: {
    textAlign: 'center',
    padding: '8px 10px 16px 10px',
  },
});

const formatPrice = (currency: Currency, price: string | number, offerLabel = 'Offer', isOfferHighlight = false) => {
  if (!currency || !price) {
    return 'No amount suggested.';
  }

  return (
    <React.Fragment>
      <OfferLabel isOfferHighlight={isOfferHighlight}>{offerLabel}</OfferLabel>
      <PriceLabel isOfferHighlight={isOfferHighlight}>
        {currency.initial}
        {String.fromCharCode(currency.label)}
      </PriceLabel>
      <Price isOfferHighlight={isOfferHighlight}>{price}</Price>
    </React.Fragment>
  );
};

const renderOfferIcon = () => <generalIcons.offers.genOffer />;

const renderRequestIcon = () => <generalIcons.offers.request />;

const setTriggerOfferAction = (_e: React.MouseEvent<SVGSVGElement, MouseEvent>, offerStatus: string) => {
  switch (offerStatus) {
    case 'ignore':
      ignoreOffer();
      break;
    case 'counter':
      counterOffer();
      break;
    case 'accept':
      acceptOffer();
      break;
    case 'cancel':
      cancelOffer();
      break;
    default:
      return;
  }
};

const mapOfferRefId = (offerTypes: string, index: number) => {
  switch (offerTypes) {
    case 'offer_new':
      return [
        `triggerIgnoreOfferHighlight${index}`,
        `triggerCounterOfferHighlight${index}`,
        `triggerAcceptOfferHighlight${index}`,
      ];
    case 'suggestion_new':
      return [`triggerIgnoreOfferHighlight${index}`, `triggerCounterRequestHighlight${index}`];
    case 'offer_pending':
      return `triggerCancelOfferHighlight${index}`;
    case 'suggestion_pending':
      return `triggerCancelSuggestHighlight${index}`;
    default:
      return;
  }
};

const mapOfferStatus = (offerTypes: string) => {
  switch (offerTypes) {
    case 'offer_new':
      return ['ignore', 'counter', 'accept'];
    case 'suggestion_new':
      return ['ignore', 'counter'];
    case 'offer_pending':
      return 'cancel';
    case 'suggestion_pending':
      return 'cancel';
    default:
      return;
  }
};

const OfferListItem = ({
  index,
  avatar,
  currency,
  price,
  username,
  date,
  age,
  location,
  accountHashId,
  acceptOffer,
  ignoreOffer,
  cancelOffer,
  cancelSuggestion,
  offerType,
  counterOfferDisabled,
  currentUserIsGenerous,
  isOfferHighlight,
  toggleFav,
  isFavorite,
  counterOffer,
  checkProfile,
  getNextOfferQueue,
  counterSuggestOffer,
  isFetching,
}: IOfferListItemProps): any => {
  const [favorited, setFavorited] = useState(isFavorite);
  const [hasOfferAction, setHasOfferAction] = useState(false);
  const [clickCounterAction, setClickCounterAction] = useState(false);
  const [clickProfileAction, setClickProfileAction] = useState(false);
  const [clickSuggestAction, setClickSuggestAction] = useState(false);
  const [hasSwipeAction, setHasSwipeAction] = useState(false);

  const inputAccept: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const inputSuggest: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const isMobileTablet = getBrowserDetails().isTablet || getBrowserDetails().isIpad;
  const highlightMobile: boolean = !isMobileTablet && isOfferHighlight;
  const showOfferDetails = getBrowserDetails().isTablet || getBrowserDetails().isIpad || getBrowserDetails().isDesktop;
  const ignoreOfferButton: React.MutableRefObject<HTMLElement | null> = useRef(null);
  const counterOfferButton: React.MutableRefObject<HTMLElement | null> = useRef(null);
  const cancelOfferButton: React.MutableRefObject<HTMLElement | null> = useRef(null);
  const acceptOfferButton: React.MutableRefObject<HTMLElement | null> = useRef(null);

  const navigate = useNavigate();

  const setOfferRef = (id: string, status: string | string[] | 'cancel') => {
    switch (status) {
      case 'ignore':
        ignoreOfferButton.current = document.getElementById(id);
        return ignoreOfferButton;
      case 'counter':
        counterOfferButton.current = document.getElementById(id);
        return counterOfferButton;
      case 'cancel':
        cancelOfferButton.current = document.getElementById(id);
        return cancelOfferButton;
      case 'accept':
        acceptOfferButton.current = document.getElementById(id);
        return acceptOfferButton;
      default:
        return;
    }
  };

  useEffect(() => {
    if ((clickProfileAction || clickSuggestAction || clickCounterAction) && typeof checkProfile === 'function') {
      checkProfile(accountHashId).then(response => {
        if (typeof response === 'undefined') {
          getNextOfferQueue && getNextOfferQueue();
          return;
        } else if (clickCounterAction) {
          counterOffer();
          return;
        } else if (clickSuggestAction) {
          counterSuggestOffer && counterSuggestOffer();
          return;
        }

        if (!hasOfferAction) {
          navigate(`/profile/${accountHashId}`);
        }
      });
    }

    setHasOfferAction(false);
    setHasSwipeAction(false);
    setClickCounterAction(false);
    setClickSuggestAction(false);
    setClickProfileAction(false);
  }, [
    hasSwipeAction,
    hasOfferAction,
    clickCounterAction,
    clickProfileAction,
    offerType,
    accountHashId,
    checkProfile,
    clickSuggestAction,
    counterOffer,
    counterSuggestOffer,
    getNextOfferQueue,
  ]);

  useEffect(() => {
    const currOfferTypes = mapOfferRefId(offerType, index);
    const offerStatus = mapOfferStatus(offerType);

    if (typeof currOfferTypes === 'object') {
      currOfferTypes.forEach((property, i) => {
        if (offerStatus && !_isEmpty(offerStatus)) {
          triggerTracking(offerStatus[i], setOfferRef(property, offerStatus[i]), setTriggerOfferAction);
        }
      });
    } else if (typeof currOfferTypes === 'string') {
      if (offerStatus && !_isEmpty(offerStatus)) {
        triggerTracking(offerStatus, setOfferRef(currOfferTypes, offerStatus), setTriggerOfferAction);
      }
    }
  }, [index, offerType]);

  const handleIgnoreOffer = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
    ignoreOffer();
  };

  const handleCancelOffer = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
    cancelOffer();
  };

  const handleCancelSuggestion = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    cancelSuggestion();
  };

  const handleCounter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
    setClickCounterAction(true);
  };

  const handleAcceptOffer = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
    acceptOffer();
  };

  const handleSuggestOffer = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
  };

  const handleFavUser = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isFetching) return;

    setHasOfferAction(true);
    setFavorited(!favorited);
    toggleFav && toggleFav(accountHashId);
  };

  const renderActionButtons = () => {
    switch (offerType) {
      case 'offer_new':
        return (
          <React.Fragment>
            <OfferIcons
              id={`triggerIgnoreOfferHighlight${index}`}
              data-test-id="trigger-ignore-offer-highlight"
              onClick={handleIgnoreOffer}
              isOfferHighlight={isOfferHighlight}
            >
              <generalIcons.ignore.highlight />
            </OfferIcons>
            {isOfferHighlight ? (
              <OfferIcons
                id={`triggerCounterOfferHighlight${index}`}
                data-test-id="trigger-counter-offer-highlight"
                disabled={counterOfferDisabled}
                onClick={handleCounter}
                isOfferHighlight={isOfferHighlight}
              >
                <generalIcons.offers.highlight />
              </OfferIcons>
            ) : (
              <OfferIcons
                id={`triggerCounterOfferHighlight${index}`}
                data-test-id="trigger-counter-offer-highlight"
                isOfferHighlight={isOfferHighlight}
              >
                <Link to={!counterOfferDisabled && `/profile/${accountHashId}?fromOfferList=true&isCounter=true`}>
                  <generalIcons.offers.highlight />
                </Link>
              </OfferIcons>
            )}
            <OfferIcons
              id={`triggerAcceptOfferHighlight${index}`}
              data-test-id="trigger-accept-offer-highlight"
              onClick={handleAcceptOffer}
              isOfferHighlight={isOfferHighlight}
              ref={inputAccept}
            >
              <generalIcons.accept.highlight />
            </OfferIcons>
          </React.Fragment>
        );
      case 'suggestion_new':
        return (
          <React.Fragment>
            <OfferIcons
              id={`triggerIgnoreOfferHighlight${index}`}
              data-test-id="trigger-ignore-offer-highlight"
              onClick={handleIgnoreOffer}
              isOfferHighlight={isOfferHighlight}
            >
              <generalIcons.ignore.highlight />
            </OfferIcons>
            {isOfferHighlight ? (
              <OfferIcons
                id={`triggerCounterRequestHighlight${index}`}
                data-test-id="trigger-counter-request-highlight"
                onClick={handleSuggestOffer}
                isOfferHighlight={isOfferHighlight}
                ref={inputSuggest}
              >
                <generalIcons.suggest.highlight />
              </OfferIcons>
            ) : (
              <OfferIcons
                id={`triggerCounterRequestHighlight${index}`}
                data-test-id="trigger-counter-request-highlight"
                isOfferHighlight={isOfferHighlight}
              >
                <Link to={`/profile/${accountHashId}?fromOfferList=true`}>
                  {ternaryFunc(currentUserIsGenerous, renderOfferIcon(), renderRequestIcon())}
                </Link>
              </OfferIcons>
            )}
          </React.Fragment>
        );
      case 'offer_pending':
        return (
          <React.Fragment>
            <OfferIcons
              id={`triggerCancelOfferHighlight${index}`}
              data-test-id="trigger-cancel-offer-highlight"
              onClick={handleCancelOffer}
              isOfferHighlight={isOfferHighlight}
            >
              <generalIcons.ignore.cancel />
            </OfferIcons>
          </React.Fragment>
        );
      case 'suggestion_pending':
        return (
          <React.Fragment>
            <OfferIcons
              id={`triggerCancelSuggestHighlight${index}`}
              data-test-id="trigger-cancel-suggest-highlight"
              onClick={handleCancelSuggestion}
              isOfferHighlight={isOfferHighlight}
            >
              <generalIcons.ignore.cancel />
            </OfferIcons>
          </React.Fragment>
        );

      default:
        return null;
    }
  };

  const getOfferDescription = () => {
    switch (offerType) {
      case 'suggestion_new':
        return ' wants to go on a date!';
      case 'suggestion_pending':
        return ' is considering a price';
      case 'offer_pending':
        return ' is considering your offer';

      default:
        return null;
    }
  };

  const offerHighlightDetails = () => {
    const offerDescription = getOfferDescription();

    return (
      <DetailsActionsContainer className="css-offer-profile-details">
        <OfferDetails data-test-id="offer-highlight-info" isOfferHighlight={isOfferHighlight}>
          <HighlightFavoriteButton
            style={{ float: 'right' }}
            favorited={favorited}
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleFavUser(e)}
          />
          <Username data-test-id="offer-highlight-username">{username}</Username>{' '}
          <OfferDescription data-test-id="offer-highlight-desc">{offerDescription}</OfferDescription> &bull;{' '}
          {tsSince(date)}
          <br />
          {age} &bull; {location}
        </OfferDetails>
      </DetailsActionsContainer>
    );
  };

  const renderOfferDetails = () => {
    const offerDescription = getOfferDescription();

    if (isOfferHighlight) {
      return (
        <DetailsActionsContainer className="css-offer-profile-details">
          {(showOfferDetails || !highlightMobile) && (
            <OfferDetails data-test-id="offer-highlight-info" isOfferHighlight={isOfferHighlight}>
              <HighlightFavoriteButton
                style={{ float: 'right' }}
                favorited={favorited}
                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleFavUser(e)}
              />
              <Username data-test-id="offer-highlight-username">{username}</Username>{' '}
              <OfferDescription data-test-id="offer-highlight-desc">{offerDescription}</OfferDescription> &bull;{' '}
              {tsSince(date)}
              <br />
              {age} &bull; {location}
            </OfferDetails>
          )}
          <HighlightsButtonGroup>
            <ButtonGroup>
              <OfferPriceDesktop className="css-offer-profile-price-container-desktop">
                <div className="css-offer-profile-price">
                  {formatPrice(currency, price, 'First Date Incentive Offer', isOfferHighlight)}{' '}
                </div>
              </OfferPriceDesktop>
              {renderActionButtons()}
            </ButtonGroup>
          </HighlightsButtonGroup>
        </DetailsActionsContainer>
      );
    }

    return (
      <DetailsActionsContainer className="css-offer-profile-details">
        <OfferDetails>
          <Username>{username}</Username> <OfferDescription>{offerDescription}</OfferDescription> &bull; {tsSince(date)}
          <br />
          {age} &bull; {location}
        </OfferDetails>
        <ButtonGroupContainer>
          <ButtonGroup>{renderActionButtons()}</ButtonGroup>
        </ButtonGroupContainer>
      </DetailsActionsContainer>
    );
  };

  return (
    <React.Fragment>
      <ItemContainer isOfferHighlight={isOfferHighlight} className="css-offer-profile-list-item-desktop">
        <Link
          className="css-offer-profile-list-item-link-desktop"
          onClick={() => {
            setClickProfileAction(true);
          }}
          to="#"
        >
          <Item isOfferHighlight={isOfferHighlight}>
            <Avatar
              className="css-offer-profile-avatar-desktop"
              src={avatar}
              customStyle={{ marginRight: isOfferHighlight ? 10 : 16 }}
              width={isOfferHighlight ? 240 : 96}
            />
            {showOfferDetails && renderOfferDetails()}
            {!isOfferHighlight && (
              <OfferPrice className="css-offer-profile-price-container-desktop">
                <div className="css-offer-profile-price">{formatPrice(currency, price)} </div>
              </OfferPrice>
            )}
            <OfferPriceMobile className="css-offer-profile-price-container-desktop">
              <div className="css-offer-profile-price">{formatPrice(currency, price)} </div>
            </OfferPriceMobile>
          </Item>
        </Link>
      </ItemContainer>

      <ItemContainerMobile data-test-id="offer-highlight-profile-link" className="css-offer-profile-list-item-mobile">
        <Link className="css-offer-profile-list-item-link-mobile" to={`/profile/${accountHashId}`}>
          <StyledSwipeContainer highlightMobile={highlightMobile}>
            {highlightMobile && !hasSwipeAction && (
              <div>
                <CardTop>
                  <CardOfferPrice className="css-offer-profile-price-container-mobile">
                    <div className="css-offer-profile-price" data-test-id="offer-highlights-price">
                      {formatPrice(currency, price)}{' '}
                    </div>
                  </CardOfferPrice>
                  <Gradient />
                  <Avatar
                    data-test-id="offer-highlight-avatar"
                    className="css-offer-profile-avatar-mobile"
                    src={avatar}
                    width={'100%'}
                  />
                </CardTop>
                <CardBottom>{offerHighlightDetails()}</CardBottom>
                <CardBottom>{renderOfferDetails()}</CardBottom>
              </div>
            )}
          </StyledSwipeContainer>
          <Card isOfferHighlight={isOfferHighlight}>
            {!isOfferHighlight && (
              <CardTop>
                <CardOfferPrice className="css-offer-profile-price-container-mobile">
                  <div className="css-offer-profile-price" data-test-id="offer-highlights-price">
                    {formatPrice(currency, price)}{' '}
                  </div>
                </CardOfferPrice>
                <Gradient />
                <Avatar
                  data-test-id="offer-highlight-avatar"
                  className="css-offer-profile-avatar-mobile"
                  src={avatar}
                  width={'100%'}
                />
              </CardTop>
            )}
            {!highlightMobile && <CardBottom>{renderOfferDetails()}</CardBottom>}
          </Card>
        </Link>
      </ItemContainerMobile>
    </React.Fragment>
  );
};

export default OfferListItem;
